
import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Award } from 'lucide-react';

interface CertificationsTabProps {
  userId: string;
}

const CertificationsTab: React.FC<CertificationsTabProps> = ({ userId }) => {
  const { toast } = useToast();
  const [certifications, setCertifications] = useState([]);
  const [newCertification, setNewCertification] = useState({
    name: '',
    issuingOrganization: '',
    issueDate: '',
    expiryDate: '',
    credentialId: '',
    credentialUrl: '',
  });

  useEffect(() => {
    if (userId) {
      fetchCertifications();
    }
  }, [userId]);

  const fetchCertifications = async () => {
    try {
      const { data, error } = await supabase
        .from('user_certifications')
        .select('*')
        .eq('user_id', userId)
        .order('issue_date', { ascending: false });

      if (error) throw error;
      setCertifications(data || []);
    } catch (error) {
      console.error('Error fetching certifications:', error.message);
    }
  };

  const handleCertificationChange = (e) => {
    const { name, value } = e.target;
    setNewCertification({
      ...newCertification,
      [name]: value,
    });
  };

  const handleAddCertification = async (e) => {
    e.preventDefault();
    
    if (!userId) {
      toast({
        title: "Error",
        description: "User not authenticated",
        variant: "destructive",
      });
      return;
    }
    
    if (!newCertification.name || !newCertification.issuingOrganization) {
      toast({
        title: "Required fields missing",
        description: "Certificate name and issuing organization are required.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('user_certifications')
        .insert({
          user_id: userId,
          name: newCertification.name,
          issuing_organization: newCertification.issuingOrganization,
          issue_date: newCertification.issueDate || null,
          expiry_date: newCertification.expiryDate || null,
          credential_id: newCertification.credentialId || null,
          credential_url: newCertification.credentialUrl || null,
        });

      if (error) throw error;

      setNewCertification({
        name: '',
        issuingOrganization: '',
        issueDate: '',
        expiryDate: '',
        credentialId: '',
        credentialUrl: '',
      });

      fetchCertifications();

      toast({
        title: "Certification added",
        description: "Your certification has been successfully added.",
      });
    } catch (error) {
      console.error('Error adding certification:', error.message);
      toast({
        title: "Failed to add certification",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDeleteCertification = async (id) => {
    try {
      const { error } = await supabase
        .from('user_certifications')
        .delete()
        .eq('id', id);

      if (error) throw error;

      fetchCertifications();

      toast({
        title: "Certification deleted",
        description: "Your certification has been successfully deleted.",
      });
    } catch (error) {
      console.error('Error deleting certification:', error.message);
      toast({
        title: "Failed to delete certification",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="border border-[#0EA5E9]/20 shadow-md bg-white/80">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Award className="h-5 w-5 text-[#0EA5E9]" />
          Certifications
        </CardTitle>
        <CardDescription>
          Add your professional certifications
        </CardDescription>
      </CardHeader>
      <CardContent>
        {certifications.length > 0 ? (
          <div className="space-y-4 mb-8">
            <h3 className="text-lg font-medium text-gray-800">Your Certifications</h3>
            <div className="space-y-4">
              {certifications.map((cert) => (
                <div key={cert.id} className="p-4 bg-white border border-[#0EA5E9]/30 rounded-md">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium text-gray-800">{cert.name}</h4>
                      <p className="text-gray-600">{cert.issuing_organization}</p>
                      {cert.issue_date && (
                        <p className="text-sm text-gray-600">
                          Issued: {new Date(cert.issue_date).toLocaleDateString()}
                          {cert.expiry_date && ` • Expires: ${new Date(cert.expiry_date).toLocaleDateString()}`}
                        </p>
                      )}
                      {cert.credential_id && (
                        <p className="text-sm text-gray-600 mt-1">Credential ID: {cert.credential_id}</p>
                      )}
                      {cert.credential_url && (
                        <a 
                          href={cert.credential_url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-sm text-[#0EA5E9] hover:underline mt-1 inline-block"
                        >
                          View Credential
                        </a>
                      )}
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-gray-600 hover:text-gray-800 hover:bg-red-500/20"
                      onClick={() => handleDeleteCertification(cert.id)}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-6 mb-8">
            <p className="text-gray-600">You haven't added any certifications yet</p>
          </div>
        )}

        <div className="border-t border-[#0EA5E9]/20 pt-6">
          <h3 className="text-lg font-medium text-gray-800 mb-4">Add a New Certification</h3>
          <form onSubmit={handleAddCertification} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="certName">Certificate Name*</Label>
                <Input
                  id="certName"
                  name="name"
                  placeholder="e.g., AWS Certified Solutions Architect"
                  value={newCertification.name}
                  onChange={handleCertificationChange}
                  className="border-[#0EA5E9]/30"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="issuingOrganization">Issuing Organization*</Label>
                <Input
                  id="issuingOrganization"
                  name="issuingOrganization"
                  placeholder="e.g., Amazon Web Services"
                  value={newCertification.issuingOrganization}
                  onChange={handleCertificationChange}
                  className="border-[#0EA5E9]/30"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="issueDate">Issue Date</Label>
                <Input
                  id="issueDate"
                  name="issueDate"
                  type="date"
                  value={newCertification.issueDate}
                  onChange={handleCertificationChange}
                  className="border-[#0EA5E9]/30"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expiryDate">Expiry Date</Label>
                <Input
                  id="expiryDate"
                  name="expiryDate"
                  type="date"
                  value={newCertification.expiryDate}
                  onChange={handleCertificationChange}
                  className="border-[#0EA5E9]/30"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="credentialId">Credential ID</Label>
                <Input
                  id="credentialId"
                  name="credentialId"
                  placeholder="e.g., AWS-ASA-12345"
                  value={newCertification.credentialId}
                  onChange={handleCertificationChange}
                  className="border-[#0EA5E9]/30"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="credentialUrl">Credential URL</Label>
                <Input
                  id="credentialUrl"
                  name="credentialUrl"
                  placeholder="e.g., https://www.credly.com/badges/..."
                  value={newCertification.credentialUrl}
                  onChange={handleCertificationChange}
                  className="border-[#0EA5E9]/30"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="bg-[#0EA5E9] text-white hover:bg-[#0EA5E9]/90 w-full"
            >
              Add Certification
            </Button>
          </form>
        </div>
      </CardContent>
    </Card>
  );
};

export default CertificationsTab;
